import { useFormik } from 'formik';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { BackArrow } from '../../../assets/icons';
import { Button } from '../../../components';
import { DisplayField } from '../../../components/DisplayForms';
import DisplayForm from '../../../components/DisplayForms/DisplayForm';
import { logError } from '../../../components/LogError';
import { appIsWorkingVar } from '../../../graphql/cache';
import { getFieldAttributes } from '../../../helpers';
import { NavigationPath } from '../../../types/DomainTypes';
import { buildDisplayText, classNames, validateEmail } from '../../../utils';
import AuthLayout from '../components/AuthLayout';
import ShowTextIn from '../components/ShowTextIn';
import { executePasswordResetRequest, getLoginValidationSchema } from '../helpers';
import usePasswordMutation from '../hooks/usePasswordMutation';
import { LoginModelProp } from '../types';

enum DisplayTextKeys {
  TITLE = 'forgotPasswordTitle',
  EMAIL_SENT_VIEW_TITLE = 'checkEmailTitle',
  EMAIL_SENT_VIEW_SUBTITLE = 'checkEmailBody',
  SUBTITLE = 'subTitle',
  REGISTER_LINK = 'registerLink',
  FORGOT_PASSWORD_LINK = 'forgotPasswordLink',
  FAILED_TO_LOGIN = 'failedToLogin',
  BUTTON = 'button',
  EMAIL_SENT_VIEW_CTA = 'checkEmailCallToAction',
  FIND_OUT_MORE = 'findOutMore',
  ERROR_MESSAGE = 'error',
  EMAIL_LABEL = 'auth:emailAddress.label',
  EMAIL_PLACEHOLDER = 'auth:emailAddress.required',
  RESEND_EMAIL_LINK = 'resendEmailLink',
}

const resendEmailMaxTime = 10;
const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { resetPassword } = usePasswordMutation();
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isEmailSent, setIsEmailSent] = useState<boolean>(false);
  const [isFormValid, setFormValidation] = React.useState(false);
  const [showResubmitText, setShowResubmitText] = useState(false);
  const displayText = React.useMemo(
    () => buildDisplayText(Object.values(DisplayTextKeys), 'auth:resetPassword', t),
    [t],
  );

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    e.preventDefault();
    formik.handleChange(e);
    const { value } = e.target;
    setFormValidation(validateEmail(value));
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    formik.handleBlur(e);
    const { value } = e.target;
    setFormValidation(validateEmail(value));
  };
  const onBack = (e: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
    e.preventDefault();
    resetState();
    navigate(NavigationPath.LOGIN);
  };

  const resetState = () => {
    appIsWorkingVar(false);
    setIsEmailSent(false);
    setErrorMessage(null);
    setIsLoggingIn(false);
    formik.resetForm();
  };

  const recoverPassword = async ({ email }: { email: string }) => {
    executePasswordResetRequest(email, {
      appIsWorkingVar,
      resetPassword,
      onEmailSent: (sent: boolean) => {
        setIsLoggingIn(false);
        setIsEmailSent(sent);
      },
      onError: (message) => {
        setErrorMessage(displayText[DisplayTextKeys.ERROR_MESSAGE]);
        logError(message);
        setIsLoggingIn(false);
        setShowResubmitText(true);
      },
    });
  };
  const mValidationSchema = yup.object({
    [LoginModelProp.EMAIL]: getLoginValidationSchema(t)[LoginModelProp.EMAIL],
  });

  const formik = useFormik({
    initialValues: { [LoginModelProp.EMAIL]: '' },
    onSubmit: async (values) => {
      if (isEmailSent) navigate(NavigationPath.LOGIN);
      else {
        setIsLoggingIn(true);
        recoverPassword(values);
      }
    },
    validationSchema: mValidationSchema,
  });

  const recoveryForm = useMemo(() => {
    const emailHasError = formik.touched[LoginModelProp.EMAIL] && formik.errors[LoginModelProp.EMAIL];
    const emailAttrs = getFieldAttributes(
      LoginModelProp.EMAIL,
      LoginModelProp.EMAIL,
      displayText[DisplayTextKeys.EMAIL_LABEL],
    );
    const emailField = {
      ...emailAttrs,
      isAutoFocus: true,
      disabled: isLoggingIn,
      placeholder: displayText[DisplayTextKeys.EMAIL_PLACEHOLDER],
      inputProps: {
        showClearButton: true,
        inputClassName:
          'bg-transparent dark:bg-transparent text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-500',
        inputContainerClassName: classNames(
          'rounded-xl border-2 transition-all duration-200',
          emailHasError
            ? '!border-red-400 dark:!border-red-500'
            : '!border-gray-200 dark:!border-slate-600 focus-within:!border-orange dark:focus-within:!border-teal-500',
        ),
      },
      onChange: handleChange,
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        handleBlur(e);
      },
      onClear: (dField?: DisplayField) => {
        setFormValidation(false);
        formik.setFieldValue(dField!.id as string, '');
      },

      helperText: emailHasError ? formik.errors[LoginModelProp.EMAIL] : '',
      label: {
        ...emailAttrs.label,
        className: classNames(
          'text-sm font-medium mb-1.5',
          emailHasError ? 'text-red-500 dark:text-red-400' : 'text-gray-700 dark:text-gray-300',
        ),
      },
    };

    return [
      {
        className: 'w-full gap-5',
        fields: [emailField],
      },
    ];

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik]);

  const displayInfo = {
    title: isEmailSent ? (
      displayText[DisplayTextKeys.EMAIL_SENT_VIEW_TITLE]
    ) : (
      <div id="title-container" className="flex items-center justify-center w-full max-w-[420px] px-4">
        <button
          className="mr-4 p-2 rounded-full hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors"
          onClick={(e) => onBack(e as unknown as React.MouseEvent<SVGSVGElement, MouseEvent>)}
          aria-label="Go back"
        >
          <BackArrow className="w-5 h-5 text-gray-600 dark:text-gray-300" data-testid="back-arrow-button" />
        </button>
        <h5 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-white">
          {displayText[DisplayTextKeys.TITLE]}
        </h5>
      </div>
    ),

    subTitle: displayText[isEmailSent ? DisplayTextKeys.EMAIL_SENT_VIEW_SUBTITLE : DisplayTextKeys.SUBTITLE],
  };
  const isSubmitDisabled = showResubmitText || (!isEmailSent && !isFormValid);

  return (
    <AuthLayout
      error={{ body: errorMessage, onClose: () => setErrorMessage('') }}
      title={displayInfo.title}
      subTitle={displayInfo.subTitle}
      classNames={{
        subTitle: 'w-full max-w-[420px] px-4',
      }}
    >
      <div className="w-full max-w-[420px] px-4 sm:px-0">
        {/* Form Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl dark:shadow-slate-900/50 p-6 sm:p-8 mt-6 sm:mt-8 border border-gray-100 dark:border-slate-700/50">
          {!isEmailSent && (
            <DisplayForm
              titleContainerClassName="mt-0"
              sectionsContainerClassName="pb-1"
              sections={recoveryForm}
              title=""
              model={{ [LoginModelProp.EMAIL]: formik.values[LoginModelProp.EMAIL], modelType: 'login' }}
            />
          )}

          {/* Success Icon for Email Sent */}
          {isEmailSent && (
            <div className="flex flex-col items-center py-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-100 to-green-50 dark:from-green-900/30 dark:to-green-800/20 flex items-center justify-center mb-4">
                <svg
                  className="w-8 h-8 text-green-500 dark:text-green-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-center text-gray-600 dark:text-gray-300 text-sm">
                {t('auth:resetPassword.checkEmailInstruction')}
              </p>
            </div>
          )}

          <Button
            onClick={formik.handleSubmit}
            type="submit"
            isDisable={isSubmitDisabled}
            className={`w-full mt-6 h-12 text-base font-semibold rounded-xl transition-all duration-200 ${
              isSubmitDisabled
                ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-slate-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/30 dark:shadow-teal-500/30 hover:shadow-xl hover:shadow-orange/40 dark:hover:shadow-teal-500/40 hover:scale-[1.02] active:scale-[0.98]'
            }`}
            isProcessing={isLoggingIn}
            props={{
              name: 'reset-password',
            }}
          >
            {displayText[isEmailSent ? DisplayTextKeys.EMAIL_SENT_VIEW_CTA : DisplayTextKeys.BUTTON]}
          </Button>
        </div>

        {showResubmitText && (
          <div className="flex justify-center mt-6 text-gray-500 dark:text-gray-400">
            <ShowTextIn
              translationKey={displayText[DisplayTextKeys.RESEND_EMAIL_LINK]}
              initValue={resendEmailMaxTime}
              onEnd={() => setShowResubmitText(false)}
            />
          </div>
        )}
      </div>
    </AuthLayout>
  );
};

export default ResetPassword;
